import { useState } from 'react';
import { api } from '@/services/api';
import { storeSession } from '@/services/authSession';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';

export function ChangePasswordModal({ onSuccess }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    try {
      const result = await api.changePassword(formData.currentPassword, formData.newPassword);
      // The backend bumps token_version on password change, which invalidates
      // every previous token; keep the freshly issued pair so the user is not
      // logged out right after changing the password.
      storeSession(result);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    }
    setLoading(false);
  };

  const togglePassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 modal-backdrop" data-testid="change-password-modal">
      <div className="modal-card w-full max-w-md overflow-y-auto rounded-2xl border border-[color:color-mix(in_srgb,var(--border)_65%,transparent)] bg-surface shadow-[0_18px_50px_rgba(0,0,0,0.18)] sm:max-h-[calc(100dvh-2rem)]">
        <div className="p-4 sm:p-6">
          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[color:color-mix(in_srgb,var(--border)_55%,transparent)] bg-[color:color-mix(in_srgb,var(--accent-yellow)_14%,var(--surface))] text-foreground">
              <Lock className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 pt-0.5">
              <h2 className="font-heading text-xl font-semibold leading-6 text-foreground">Cambiar Contraseña</h2>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">Por seguridad, debés cambiar tu contraseña temporal</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm leading-5 text-destructive" data-testid="change-password-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Contraseña actual (temporal)</label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  className="w-full rounded-xl border border-[color:color-mix(in_srgb,var(--border)_70%,transparent)] bg-muted px-4 py-3 pr-12 text-sm text-foreground outline-none transition focus:border-[#3A9CC8] focus:ring-2 focus:ring-[#3A9CC8]/20 placeholder:text-muted-foreground"
                  placeholder="La contraseña que te dieron"
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => togglePassword('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
                >
                  {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Nueva contraseña</label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className="w-full rounded-xl border border-[color:color-mix(in_srgb,var(--border)_70%,transparent)] bg-muted px-4 py-3 pr-12 text-sm text-foreground outline-none transition focus:border-[#3A9CC8] focus:ring-2 focus:ring-[#3A9CC8]/20 placeholder:text-muted-foreground"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => togglePassword('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
                >
                  {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Confirmar nueva contraseña</label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full rounded-xl border border-[color:color-mix(in_srgb,var(--border)_70%,transparent)] bg-muted px-4 py-3 pr-12 text-sm text-foreground outline-none transition focus:border-[#3A9CC8] focus:ring-2 focus:ring-[#3A9CC8]/20 placeholder:text-muted-foreground"
                  placeholder="Repetí la nueva contraseña"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => togglePassword('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
                >
                  {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.newPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                <p className="mt-1 flex items-center gap-1 text-xs text-accent-mint">
                  <Check className="w-3 h-3" /> Las contraseñas coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-heading text-sm font-semibold text-primary-foreground transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#3A9CC8]/30 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="submit-change-password"
            >
              <Lock className="w-4 h-4" />
              {loading ? 'Guardando...' : 'Cambiar Contraseña'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs leading-4 text-muted-foreground opacity-75">
            Tu contraseña será encriptada de forma segura
          </p>
        </div>
      </div>
    </div>
  );
}
